/**
 * Confirmation screen for workflow legal ID — shows extracted ID data.
 * Separate from IVA's ConfirmationScreen.
 *
 * Renders whatever attributes come back from the WE poll response.
 * The exact fields depend on what the WE returns after Veriff processing.
 */

import { useTranslation } from "react-i18next";
import styles from "../legalIdVerification/LegalIdVerification.module.scss";

interface Props {
  /** Extracted user attributes from WE response (ux.data or a fetched link) */
  attributes: Record<string, unknown>;
  onConfirm: () => void;
  onRetake: () => void;
}

export function WorkflowConfirmationScreen({ attributes, onConfirm, onRetake }: Props) {
  const { t } = useTranslation();

  // Map of attribute keys to translated labels. Keys come from the WE response;
  // the same human-readable label may apply to multiple keys (e.g. nameFirst | firstName).
  const labelMap: Record<string, string> = {
    nameFirst: t('legalId.confirming.firstName'),
    firstName: t('legalId.confirming.firstName'),
    nameLast: t('legalId.confirming.lastName'),
    lastName: t('legalId.confirming.lastName'),
    middleName: t('legalId.confirming.middleName'),
    nameMiddle: t('legalId.confirming.middleName'),
    dateOfBirth: t('legalId.confirming.dateOfBirth'),
    dob: t('legalId.confirming.dateOfBirth'),
    documentNumber: t('legalId.confirming.documentNumber'),
    documentType: t('legalId.confirming.documentType'),
    issuingCountry: t('legalId.confirming.issuingCountry'),
    issuingState: t('legalId.confirming.issuingState'),
  };

  // Filter out internal/technical fields
  const displayFields = Object.entries(attributes).filter(
    ([key, value]) => value != null && typeof value !== "object" && labelMap[key]
  );

  return (
    <div className={styles.card}>
      <h1 className={styles.title}>{t('legalId.confirming.heading')}</h1>
      <p className={styles.subtitle}>{t('legalId.confirming.subtitle')}</p>

      <div className={styles.attributesBox}>
        {displayFields.map(([key, value]) => (
          <p className={styles.attributeRow} key={key}>
            <b className={styles.attributeLabel}>{labelMap[key] ?? key}</b>
            <br aria-hidden="true" />
            {String(value)}
          </p>
        ))}
        {displayFields.length === 0 && (
          <p style={{ color: "#666", fontSize: "14px" }}>{t('legalId.confirming.processingDetails')}</p>
        )}
      </div>

      <button className={styles.confirmButton} onClick={onConfirm} aria-label={t('legalId.confirming.confirmButtonAriaLabel')}>
        {t('legalId.confirming.confirmButton')}
      </button>
      <button className={styles.retakeButton} onClick={onRetake} aria-label={t('legalId.confirming.retakeButtonAriaLabel')}>
        {t('legalId.confirming.retakeButton')}
      </button>
    </div>
  );
}
